// src/pages/faq.js
import React, { useState, useMemo } from 'react';
import { ChevronDown, Search, HelpCircle, Copy, Newspaper, Database, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';

// Import Brazilian detector and mappings
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { faqPtBR } from '../../components/mappings/faq.pt-br';

const FAQ = () => {
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Simple translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return faqPtBR[text] || text;
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState(null);

const faqs = [
  {
    question: t("What is PickStar AI, really?"),
    answer: t("PickStar AI is your competitive edge in sports betting—an AI-powered research platform that analyzes millions of data points, events, and trends to surface high-probability picks, narrative insights, and value bets across MLB, soccer, and more. We're not a tipster service; we're a data research tool for smarter bettors."),
    keywords: ["pickstar", "ai", "platform", "betting", "research", "data", "analysis", "edge", "competitive"]
  },
  {
    question: t("How does your AI work?"),
    answer: t("Our platform uses large language models (LLMs) and real-time data pipelines to monitor everything from player injuries and lineup changes to sharp money moves and weather. We blend ML, domain expertise, and human curation to generate not just picks, but context—so you can understand *why* a bet is strong."),
    keywords: ["ai", "llm", "machine learning", "data", "real-time", "injuries", "lineup", "weather", "context", "analysis"]
  },
  {
    question: t("Is PickStar AI legal to use?"),
    answer: t("Yes. PickStar AI is an informational service, not a bookmaker or gambling operator. We provide research, not betting services. You remain responsible for ensuring you comply with local laws."),
    keywords: ["legal", "law", "informational", "research", "compliance", "bookmaker", "gambling", "operator"]
  },
  {
    question: t("How accurate are your picks?"),
    answer: t("We're radically transparent: win rates, historical results, and even our models' biases are published openly. No \"get rich quick\" nonsense—just cold, hard stats and up-to-date win rates. See the results page for all streaks, good and bad."),
    keywords: ["accuracy", "win rates", "transparent", "results", "statistics", "historical", "bias", "streaks"]
  },
  {
    question: t("Who is PickStar AI for?"),
    answer: t("Serious bettors who want a data-driven edge. Casual fans who want smarter action. Anyone tired of hype and looking for clear, AI-powered reasoning. (If you just want to \"tail picks,\" you'll benefit; if you want to **learn to win smarter**, you'll thrive.)"),
    keywords: ["serious bettors", "casual fans", "data-driven", "edge", "reasoning", "learn", "smarter", "thrive"]
  },
  {
    question: t("What sports do you cover?"),
    answer: t("Our core is MLB and soccer (Brazil Série A, Copa, and US leagues), but we're constantly expanding based on user demand—NFL, NBA, and more on the roadmap."),
    keywords: ["sports", "mlb", "soccer", "brasil", "copa", "nfl", "nba", "coverage", "expanding", "roadmap"]
  },
  {
    question: t("How much does it cost?"),
    answer: t("Everyone gets one free Game Simulation per day. Pro users unlock six simulations per day, plus access to deeper breakdowns and earlier edge alerts. Want even more? StarCoins can be purchased to run extra simulations, especially on big game days."),
    keywords: ["cost", "price", "free", "simulation", "pro", "starcoins", "pricing", "subscription", "deep dives"]
  },
  {
    question: t("How does PickStar make money?"),
    answer: t("We sell access to our advanced AI research—no affiliate deals, no shady bookie partnerships. Our incentive is your long-term success, not quick churn."),
    keywords: ["revenue", "money", "research", "affiliate", "partnerships", "success", "long-term", "transparency"]
  },
  {
    question: t("Do you promote responsible gambling?"),
    answer: t("Yes, absolutely. PickStar AI is an advanced research tool, not a bookmaker—we provide data and insights to help you make informed decisions, but we don't facilitate betting itself. We encourage all users to gamble responsibly: set limits, never bet more than you can afford to lose, and seek help if gambling becomes a problem. Remember, no pick is guaranteed, and sports betting always involves risk."),
    keywords: ["responsible", "gambling", "research", "tool", "limits", "risk", "help", "informed", "decisions"]
  },
  {
    question: t("Can I get a free trial or credits?"),
    answer: t("Yes—everyone gets a daily free simulation. Plus, you can earn extra StarCoins for referrals, feedback, and select promotions. The more you contribute or win, the more you unlock."),
    keywords: ["free", "trial", "credits", "starcoins", "referrals", "feedback", "promotions", "unlock"]
  },
  {
    question: t("Is AI always right?"),
    answer: t("No, and with LLMs occasionally you get a mistake or an error in response. We've fine-tuned this extensively to avoid errors but they do happen. That's why we show our work—so you can verify the reasoning and data behind every pick."),
    keywords: ["ai", "right", "wrong", "llm", "mistakes", "errors", "fine-tuned", "reasoning", "verify"]
  },
  {
    question: t("What if I disagree with a pick or want more explanation?"),
    answer: t("Challenge the AI directly! Ask follow-up questions like 'What about [specific factor]?' or 'How does [recent event] change this?' PickStar is trained to dig deeper when prompted. Try: 'Show me alternative scenarios,' 'What's the contrarian case?' or 'Break down the risk factors.' The AI learns from your pushback and often reveals insights it didn't surface initially. Every pick shows its reasoning, but the real value comes from the conversation—treat it like interrogating a research analyst who has access to everything but sometimes needs the right questions to unlock the best insights."),
    keywords: ["disagree", "explanation", "breakdown", "data", "thought process", "challenge", "feedback", "improve"]
  },
  {
    question: t("Can I get API access or automate my workflow?"),
    answer: t("Enterprise and B2B API access are available for partners. Reach out if you want to power your own site or tool with PickStar AI insights at engineering@pickstarai.com"),
    keywords: ["api", "access", "enterprise", "b2b", "partners", "automation", "workflow", "engineering"]
  },
  {
    question: t("What data and features do I get for each sport?"),
    answer: t("Not all sports get the same 'AI treatment.' Here's the real breakdown—so you know exactly what's included:\n\nNBA v2: Real-time team & player stats, rosters, advanced metrics, historical data, news, injuries, agentic AI modeling, deep 'why' behind every pick. Full game simulations, custom scenarios, data queries.\n\nNCAAB v2: Same as NBA—deep stats, rosters, news, injuries, AI models, advanced analytics. Full simulations & 'what if' queries, instant breakdowns.\n\nNCAAF v2: Same as above—full team/player data, advanced stats, injuries/news, powerful AI modeling. Everything above; deep narrative insights.\n\nNFL v2: For 2025-2026: live stats, rosters, advanced analytics, injuries/news, best-in-class AI models. Full sim, news triggers, data queries, instant explanations.\n\nFootball/Soccer v2: Fixtures, teams, shots/corners, rosters, advanced stats, historical context, live injuries/news, high-frequency modeling. Full sim, scenario exploration, news/edge alerts.\n\nMLB v2-limited: Team, roster, stats, news, injuries, solid modeling—fewer advanced/agentic features than NBA/NFL, but still way more than 'just odds'. Game sim, trend surfacing, basic custom queries.\n\nWNBA Limited v2: Team & player stats, advanced analytics, injuries/news (expanding to full v2 soon). Simulations, some deep stats, but a bit less flexible.\n\nIntl. Baseball Data Hub Only: Interactive stats by player/team, trends, historical data—no agentic AI yet. Compare players/teams, stat deep-dives, but no full sim.\n\nVolleyball Data Hub Only: League & player stats, interactive dashboards, trends. Deep stat lookup, compare teams, no picks/sims.\n\nMMA/UFC Data Hub Only: Fighter stats, matchup analysis, historical trends. Compare fighters, matchup breakdowns, no live AI sim yet.\n\nWhat does 'v2' mean? You get *more than odds*: Full stats, rosters, news, injuries, advanced analytics, and live market context. You can run *full game simulations*—not just see a pick, but see 'why' and explore custom scenarios. 'Agentic' = the AI actively watches for late-breaking news, injuries, and other edge signals—surfacing value before the market adjusts.\n\nWhat does 'Data Hub Only' mean? You get pro-grade stats and dashboards to explore, but no AI-generated picks or deep simulations (yet)."),
    keywords: ["data", "features", "sports", "v2", "agentic", "simulation", "stats", "rosters", "news", "injuries", "analytics", "nba", "ncaab", "ncaaf", "nfl", "soccer", "mlb", "wnba", "mma", "ufc", "volleyball", "baseball"]
  },
  {
    question: t("How do I handle large prop bet data for NBA?"),
    answer: t("PickStar AI can pull prop data for players, but with multiple games (e.g., 8 games × 10 players × multiple props), the payload becomes substantial and you might risk losing some data in the call. For best results, focus on a particular game first rather than requesting all available props at once. This approach will lead to more complete and accurate data for your analysis."),
    keywords: ["nba", "prop", "bets", "data", "players", "games", "payload", "focus"]
  },
  {
    question: t("How does the news search feature work?"),
    answer: t("We currently have a proprietary AI orchestration system to pull in news sources. Right now, we're limiting it to NCAA news, but we plan to add more sources in the future. You can access this feature by clicking the purple 'Search' button in the chat to find relevant articles, or use it to guide betting decisions in near real-time."),
    keywords: ["news", "search", "articles", "real-time", "ncaa", "information", "updates", "sources"]
  },
  {
    question: t("How can I combine parlays across different sports?"),
    answer: t("In v2, you can simply ask about any sport in one chat and create any combination you want. No need to copy between chats—just ask for cross-sport parlays, multi-game strategies, or any complex betting combinations all in the same conversation."),
    keywords: ["parlays", "combine", "sports", "cross-sport", "multi-game", "strategies", "v2", "combinations"]
  },
  {
    question: t("How do I copy message output?"),
    answer: t("Simply click the copy button next to the thumbs up on any message to copy its content to your clipboard. This makes it easy to save important insights or share them elsewhere."),
    keywords: ["copy", "output", "message", "clipboard", "save", "share"]
  },
  {
    question: t("How do I get started?"),
    answer: t("Create a free account to get daily free simulations. Start with our free tier to explore the platform, then upgrade to Pro for more access and advanced features when you're ready."),
    keywords: ["start", "begin", "account", "free", "simulations", "pro", "upgrade", "features"]
  },
  {
    question: t("What is the best way to query AI?"),
    answer: t("You can ask PickStar AI a variety of betting-related questions, such as:\n\n- 'What are the odds for [Team A] vs. [Team B]?' \n- 'Give me key stats for this matchup.'\n- 'Any recent news on [Team/Player]?' \n- 'What are the safest bets today?'\n- 'What high-risk plays have the best upside?'\n\nPickStar AI will retrieve relevant odds, historical data, and real-time news to help you make smarter bets."),
    keywords: ["odds", "matchup stats", "news", "betting insights", "safe bets", "high-risk plays"]
  },
  {
    question: t("Why do I need to verify my email and where is the verification email?"),
    answer: t("Email verification is a one-time security step that prevents duplicate accounts and keeps our platform secure. After signing up, we send a verification email to your registered email address.\n\n**Important: Check your spam/junk folder!** The verification email often ends up there. Look for an email from 'PickStar' with the subject 'Welcome to PickStar' or 'Verify your email'.\n\nThe verification link expires in 1 hour. If you can't find the email:\n1. Check all email folders (spam, junk, promotions, updates)\n2. Search for 'PickStar' or 'noreply@pickstarai.com'\n3. Click 'Resend Verification Email' on the verification page\n4. Add noreply@pickstarai.com to your safe senders list\n\nOnce verified, you'll have full access to PickStar AI immediately."),
    keywords: ["email", "verification", "verify", "spam", "junk", "folder", "welcome", "security", "account", "resend", "link", "expired"]
  },
  {
    question: t("How do Quick Predictions work in sports dashboards?"),
    answer: t("Quick Predictions allow you to get instant AI-powered predictions for multiple games at once. Simply select up to 5 games by clicking the prediction button on each game card. Each prediction costs 7 Star Coins per game (e.g., 3 games = 21 Star Coins). Once you've selected your games, click 'Predict Winners' to get instant AI analysis. You can submit as many batches as you want as long as you have Star Coins available."),
    keywords: ["quick", "predictions", "dashboard", "sports", "games", "star coins", "batch", "select", "instant", "ai"]
  },
  {
    question: t("Why is there a 5-game limit per prediction batch?"),
    answer: t("The 5-game maximum ensures optimal AI performance and faster response times. This limit prevents system overload and maintains prediction quality. However, you can submit unlimited batches - just predict your first 5 games, then select 5 more and predict again. As long as you have Star Coins, you can keep making predictions all day!"),
    keywords: ["limit", "5 games", "maximum", "batch", "multiple", "predictions", "performance", "unlimited"]
  },
  {
    question: t("Will my Quick Predictions be saved to chat history?"),
    answer: t("Yes! All Quick Predictions are automatically saved to your chat history. You can view them anytime by going to your main chat. Each prediction batch includes the date, selected games, AI's winner picks, confidence levels (HIGH/MED/LOW), and reasoning. This makes it easy to track your prediction history and analyze past results."),
    keywords: ["save", "saved", "history", "chat", "predictions", "track", "view", "past", "results"]
  },
  {
    question: t("How much do Quick Predictions cost?"),
    answer: t("Quick Predictions cost 7 Star Coins per game selected. For example:\n• 1 game = 7 Star Coins\n• 3 games = 21 Star Coins\n• 5 games (max per batch) = 35 Star Coins\n\nThe cost is clearly displayed before you confirm your predictions. If you don't have enough Star Coins, you'll see an upgrade notice with options to purchase more."),
    keywords: ["cost", "price", "star coins", "7 coins", "game", "batch", "purchase", "upgrade"]
  },
  {
    question: t("What should I do if my prediction shows 'N/A' or an error?"),
    answer: t("If your Quick Prediction returns 'N/A' or fails to process correctly, please take a screenshot showing:\n1. The games you selected\n2. The error or 'N/A' result\n3. Your username\n\nEmail this to support@pickstarai.com with 'Quick Prediction Error' in the subject line. We'll review your case and add the appropriate Star Coins back to your account within 14 business days. This rarely happens, but we'll always make it right!"),
    keywords: ["error", "n/a", "na", "failed", "refund", "support", "screenshot", "star coins", "problem", "issue"]
  }
];

  const filteredFaqs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return faqs;
    
    return faqs
      .filter(faq => 
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.keywords.some(keyword => keyword.includes(query))
      );
  }, [searchQuery]);

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-[#0A051E] to-emerald-900">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/50 to-transparent pointer-events-none" />

        <div className="relative min-h-screen px-4 py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <div className="mb-8">
            <Link
              to="/home"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-all duration-300 text-sm font-medium group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
              <span>Back</span>
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-emerald-300/80">
              Quick answers about PickStar AI
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-12 max-w-xl mx-auto group">
            <div className="absolute inset-0 bg-emerald-500/10 blur-xl rounded-xl
              group-hover:bg-emerald-500/20 transition-all duration-300" />
            <div className="relative">
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/10 backdrop-blur-xl border border-white/10 rounded-xl 
                  pl-11 pr-4 h-12 text-white placeholder-gray-400 
                  focus:outline-none focus:border-emerald-500/50 focus:ring-2 
                  focus:ring-emerald-500/20 transition-all duration-200"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-emerald-500/70" />
              {searchQuery && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 
                  bg-emerald-500/20 px-2 py-0.5 rounded-full text-sm text-emerald-300/90">
                  {filteredFaqs.length} {filteredFaqs.length === 1 ? 'result' : 'results'}
                </div>
              )}
            </div>
          </div>

          {/* Feature Categories */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { name: "News", icon: <Newspaper className="w-4 h-4" />, filter: "news" },
              { name: "Props", icon: <Database className="w-4 h-4" />, filter: "prop" },
              { name: "Features", icon: <Copy className="w-4 h-4" />, filter: "copy" },
              { name: "All", icon: <HelpCircle className="w-4 h-4" />, filter: "" }
            ].map((category) => (
              <button
                key={category.name}
                onClick={() => setSearchQuery(category.filter)}
                className={`flex items-center justify-center gap-2 py-2 rounded-lg transition-all duration-200
                  ${searchQuery === category.filter 
                    ? "bg-emerald-500/30 text-emerald-300 border border-emerald-500/40" 
                    : "bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10"}`}
              >
                {category.icon}
                <span>{category.name}</span>
              </button>
            ))}
          </div>

           {/* FAQs */}
           <div className="space-y-3">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-emerald-500/5 blur-sm rounded-xl 
                    transition-opacity duration-300 opacity-0 group-hover:opacity-100" 
                  />
                  <div className="relative bg-slate-800/40 backdrop-blur-sm rounded-xl 
                    overflow-hidden border border-white/10 transition-all duration-300
                    hover:border-emerald-500/30"
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <span className="font-medium text-white group-hover:text-emerald-300 
                        transition-colors duration-300">
                        {faq.question}
                      </span>
                      <ChevronDown 
                        className={`w-5 h-5 text-emerald-400 transition-all duration-300
                          ${openIndex === index ? 'rotate-180' : 'rotate-0'}
                          group-hover:scale-110`}
                      />
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out
                        ${openIndex === index ? 'max-h-[500px]' : 'max-h-0'}`}
                    >
                      <div className="p-4 text-gray-300 border-t border-white/10 
                        bg-white/5 whitespace-pre-line overflow-y-auto max-h-[500px]
                        scrollbar-thin scrollbar-thumb-emerald-600/50 scrollbar-track-white/5
                        hover:scrollbar-thumb-emerald-500/70">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 px-4 bg-white/5 backdrop-blur-sm rounded-xl 
                border border-white/10">
                <HelpCircle className="w-6 h-6 text-emerald-400 mx-auto mb-3" />
                <p className="text-gray-300">
                  {t("No results found. Try a different search or")}{" "}
                  <Link 
                    to="/help" 
                    className="text-emerald-400 hover:text-emerald-300 
                      transition-colors duration-200 hover:underline"
                  >
                    {t("contact support")}
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <Link 
              to="/help"
              className="inline-flex items-center px-5 py-2.5 bg-emerald-500/10 
                hover:bg-emerald-500/20 text-emerald-300 rounded-lg transition-all 
                duration-200 border border-emerald-500/20 hover:border-emerald-500/30
                hover:scale-105 active:scale-95"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need more help?
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default FAQ;